import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalization } from "../../../context/LocalizationContext";
import Header from "../../shared/Header/index";
import Question from "../Question/index";
import "./AssessmentDetail.css";
import check from "../../../assets/icons/check-circle.svg";
import HomeIcon from "../../../assets/icons/home.svg";
import arrow from "../../../assets/icons/arrow.svg";
import { useAppContext } from "../../../context/AppContext";
import { submitQuestionnaire } from "../../../utils/api";

const AssessmentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRTL, t } = useLocalization();
  const { assessment, userData } = location.state || {};
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    completedCategories,
    currentCategory,
    setCompletedCategories,
    categories,
    setCurrentCategory,
    savedAnswers,
    saveAnswer,
  } = useAppContext();

  useEffect(() => {
    if (!assessment) {
      navigate("/assessment");
    }
  }, [assessment, navigate]);

  useEffect(() => {
    if (assessment && savedAnswers[assessment.id]) {
      setAnswers(savedAnswers[assessment.id]);
    }
  }, [assessment, savedAnswers]);

  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [assessment?.id]);

  const handleAnswer = (questionId, answer) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer,
    };
    setAnswers(newAnswers);

    // Save answer to context for persistence
    if (assessment) {
      saveAnswer(assessment.id, questionId, answer);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < assessment.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      // Assessment completed
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Transform answers to the required format
      const formattedAnswers = [];

      Object.entries(answers).forEach(([questionId, selectedOptions]) => {
        if (Array.isArray(selectedOptions)) {
          formattedAnswers.push({
            questionId: parseInt(questionId),
            selectedOptionIds: selectedOptions,
          });
        } else {
          formattedAnswers.push({
            questionId: parseInt(questionId),
            selectedOptionId: selectedOptions,
          });
        }
      });

      // Prepare submission data
      const submissionData = {
        questionnaireId: assessment.id,
        patientId: parseInt(userData.patientId),
        answers: formattedAnswers,
      };

      // Submit to API
      await submitQuestionnaire(submissionData);

      // Update completed categories
      const updatedCompletedCategories = [
        ...completedCategories,
        assessment.id,
      ];
      setCompletedCategories(updatedCompletedCategories);

      // Determine next assessment based on current assessment
      const getNextAssessment = () => {
        if (assessment.name === "WELLNESS") {
          return "LIFESTYLE";
        } else if (assessment.name === "LIFESTYLE") {
          return "HEALTH_ASSESSMENT";
        }
        return null; // No next assessment for HEALTH_ASSESSMENT
      };

      const nextAssessmentType = getNextAssessment();

      if (nextAssessmentType) {
        // Navigate to next assessment
        navigate("/assessment", {
          state: {
            autoStartAssessment: nextAssessmentType,
            userData,
          },
        });
      } else {
        // Navigate to consultation for the final assessment
        navigate("/consultation");
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);

      // Check if it's a network connectivity issue
      if (!navigator.onLine) {
        alert(
          "No internet connection. Please check your network and try again."
        );
      } else if (error.message && error.message.includes("Failed to fetch")) {
        alert(
          "Unable to connect to the server. Please check your internet connection and try again."
        );
      } else {
        alert("Failed to submit assessment. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const CategoryTab = ({ category }) => {
    const handleCategoryClick = () => {
      // Find the assessment data for this category
      const assessmentData = categories.find((cat) => cat.id === category.id);
      if (assessmentData) {
        setCurrentCategory(category.id);
        navigate(`/assessment/${category.id}`, {
          state: {
            assessment: assessmentData,
            userData,
          },
        });
      }
    };

    return (
      <div
        className={`category-tab  ${
          completedCategories.includes(category.id) ||
          currentCategory == category.id
            ? "completed"
            : ""
        }`}
        onClick={handleCategoryClick}
        style={{ cursor: "pointer" }}
      >
        <span>{category.title}</span>
        {completedCategories.includes(category.id) && (
          <img src={check} alt="check" />
        )}
      </div>
    );
  };

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion =
    currentQuestionIndex === assessment.questions.length - 1;

  // Fix hasAnswer logic to properly handle empty arrays in multi-select questions
  const hasAnswer = (() => {
    const answer = answers[currentQuestion?.id];
    if (answer === undefined || answer === null) {
      return false;
    }
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return true;
  })();

  return (
    <div className="main-container">
      <Header text={assessment.title} userData={userData} />

      <div className="assessment-detail-content">
        <div className="category-tabs">
          {categories.map((category) => (
            <CategoryTab key={category.id} category={category} />
          ))}
        </div>

        {/* Question component */}
        {currentQuestion && (
          <Question
            question={currentQuestion}
            questionIndex={currentQuestionIndex}
            totalQuestions={assessment.questions.length}
            onAnswer={handleAnswer}
            currentAnswer={answers[currentQuestion.id]}
          />
        )}

        <div className="assessment-footer">
          <button
            className="footer-btn home-btn"
            onClick={() => navigate("/dashboard")}
          >
            <img src={HomeIcon} alt="Home" />
          </button>

          <div className="footer-btn-container">
            <button
              className="footer-btn next-btn"
              onClick={handlePrevious}
              disabled={isFirstQuestion}
            >
              <img
                src={arrow}
                alt="Next"
                style={{ transform: isRTL ? "none" : "rotate(180deg)" }}
              />

              <span>{t("previous")}</span>
            </button>

            <button
              className={
                isLastQuestion && assessment.name === "HEALTH_ASSESSMENT"
                  ? "talk-to-doctor-btn"
                  : "footer-btn next-btn"
              }
              onClick={handleNext}
              disabled={!hasAnswer || isSubmitting}
            >
              <span>
                {isSubmitting
                  ? t("submitting")
                  : isLastQuestion
                  ? assessment.name === "HEALTH_ASSESSMENT"
                    ? t("assessment.talkToDoctor")
                    : t("complete")
                  : t("next")}
              </span>
              {!isLastQuestion && !isSubmitting && (
                <img
                  src={arrow}
                  alt="Previous"
                  style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
                />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetail;
